import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import Collapse from "@material-ui/core/Collapse";
import Tooltip from "@material-ui/core/Tooltip";
import Avatar from "@material-ui/core/Avatar";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Paper from "@material-ui/core/Paper";
import Popper from "@material-ui/core/Popper";
import Typography from "@material-ui/core/Typography";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

const useStyles = makeStyles((theme) => ({
  flyout: {
    minWidth: 260,
    maxWidth: 340,
    maxHeight: "min(75vh, 640px)",
    overflowY: "auto",
    padding: "6px 0",
    borderRadius: 12,
    backgroundColor: theme.mode === "light" ? "#fff" : "#1b2431",
    boxShadow: "0 12px 32px rgba(18, 24, 40, 0.2)",
  },
  flyoutTitle: {
    padding: "8px 18px 6px",
    color: theme.mode === "light" ? "#777B8B" : "#AEB5C5",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: ".06em",
    textTransform: "uppercase",
  },
  nestedList: {
    padding: "0 0 4px 12px",
    borderLeft: `1px solid ${theme.mode === "light" ? "#E8E9F0" : "#303B4D"}`,
    marginLeft: 18,
  },
  nestedGroup: {
    minHeight: 42,
    width: "calc(100% - 20px)",
    margin: "2px 10px",
    paddingLeft: 14,
    borderRadius: 10,
    color: theme.mode === "light" ? "#4E5264" : "#E4E6EF",
    "&:hover": {
      backgroundColor: theme.mode === "light" ? "#F2F3FF" : "rgba(119,128,255,.10)",
    },
  },
  categoryLabel: {
    whiteSpace: "normal",
    lineHeight: 1.3,
  },
  expandIcon: { color: theme.palette.primary.main, marginLeft: "auto" },
}));

const hasActiveRoute = (entries, pathname) => entries.some((entry) => {
  if (entry.to) {
    const aliases = entry.to === "/agenda" ? ["/schedules"] : [];
    return pathname === entry.to || (entry.to !== "/" && pathname.startsWith(`${entry.to}/`)) || aliases.includes(pathname);
  }
  return hasActiveRoute(entry.children || [], pathname);
});

const CategoryNavigation = ({ categories, collapsed, renderLink, itemClasses }) => {
  const theme = useTheme();
  const classes = useStyles();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down(960));
  const [openCategory, setOpenCategory] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const anchorRef = useRef(null);
  const closeTimer = useRef(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = null;
  }, []);

  const closeFlyout = useCallback(() => {
    clearCloseTimer();
    setOpenCategory(null);
  }, [clearCloseTimer]);

  useEffect(() => () => clearCloseTimer(), []);

  useEffect(() => {
    if (isMobile) closeFlyout();
  }, [isMobile, closeFlyout]);

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    // Gives the pointer time to cross the small gap between the drawer and portal.
    closeTimer.current = setTimeout(() => setOpenCategory(null), 240);
  }, [clearCloseTimer]);

  const toggleGroup = (key) => {
    setExpandedGroups((current) => ({ ...current, [key]: !current[key] }));
  };

  const handleEscape = (event) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      closeFlyout();
      if (!isMobile && anchorRef.current) anchorRef.current.focus();
    }
  };

  useEffect(() => {
    if (isMobile || !openCategory) return undefined;
    const onDocumentKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        closeFlyout();
        if (anchorRef.current) anchorRef.current.focus();
      }
    };
    document.addEventListener("keydown", onDocumentKeyDown);
    return () => document.removeEventListener("keydown", onDocumentKeyDown);
  }, [isMobile, openCategory, closeFlyout]);

  useEffect(() => {
    closeFlyout();
  }, [location.pathname, location.search, closeFlyout]);

  const renderEntries = (entries, categoryKey, parentKey = "") => (
    <List dense component="div" disablePadding>
      {entries.map((entry) => {
        const key = `${categoryKey}/${parentKey}/${entry.label}`;
        if (entry.to) {
          return <React.Fragment key={key}>{renderLink(entry)}</React.Fragment>;
        }

        const expanded = !!expandedGroups[key];
        return (
          <React.Fragment key={key}>
            <ListItem
              button
              dense
              aria-expanded={expanded}
              onClick={() => toggleGroup(key)}
              onKeyDown={handleEscape}
              className={classes.nestedGroup}
            >
              {entry.icon && <ListItemIcon>{entry.icon}</ListItemIcon>}
              <ListItemText
                primary={<Typography className={itemClasses.listItemText}>{entry.label}</Typography>}
              />
              {expanded ? <ExpandLessIcon className={classes.expandIcon} /> : <ExpandMoreIcon className={classes.expandIcon} />}
            </ListItem>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <div className={classes.nestedList}>
                {renderEntries(entry.children || [], categoryKey, key)}
              </div>
            </Collapse>
          </React.Fragment>
        );
      })}
    </List>
  );

  const navigation = (
    <div aria-label="menu_categories">
      {categories.map((category) => {
        const active = hasActiveRoute(category.children, location.pathname);
        const expanded = isMobile ? openCategory === category.id : openCategory === category.id;
        const handleClick = () => {
          if (isMobile) {
            setOpenCategory((current) => current === category.id ? null : category.id);
          } else {
            clearCloseTimer();
            anchorRef.current = document.getElementById(`navigation-category-${category.id}`);
            setOpenCategory((current) => current === category.id ? null : category.id);
          }
        };

        return (
          <div
            key={category.id}
            onMouseEnter={!isMobile ? () => {
              clearCloseTimer();
              anchorRef.current = document.getElementById(`navigation-category-${category.id}`);
              setOpenCategory(category.id);
            } : undefined}
            onMouseLeave={!isMobile ? scheduleClose : undefined}
          >
            <li>
              <Tooltip title={collapsed ? category.label : ""} placement="right">
                <ListItem
                id={`navigation-category-${category.id}`}
                data-testid={`navigation-category-${category.id}`}
                button
                aria-label={category.label}
                aria-controls={!isMobile ? `navigation-panel-${category.id}` : undefined}
                aria-haspopup={isMobile ? undefined : "menu"}
                aria-expanded={expanded}
                onClick={handleClick}
                onKeyDown={handleEscape}
                className={`${itemClasses.listItem} ${active ? "active" : ""}`}
              >
                <ListItemIcon>
                  <Avatar className={`${itemClasses.iconHoverActive} ${active ? "active" : ""}`}>
                    {category.icon}
                  </Avatar>
                </ListItemIcon>
                {!collapsed && <ListItemText
                  primary={<Typography className={`${itemClasses.listItemText} ${classes.categoryLabel}`}>{category.label}</Typography>}
                />}
                {isMobile && (expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
              </ListItem>
              </Tooltip>
            </li>

            {isMobile && (
              <Collapse in={expanded} timeout="auto" unmountOnExit>
                <div className={classes.nestedList}>
                  {renderEntries(category.children, category.id)}
                </div>
              </Collapse>
            )}

            {!isMobile && (
              <Popper
                open={expanded}
                anchorEl={document.getElementById(`navigation-category-${category.id}`)}
                placement="right-start"
                style={{ zIndex: theme.zIndex.modal + 1 }}
                modifiers={{ offset: { enabled: true, offset: "0, 6" }, preventOverflow: { enabled: true, boundariesElement: "viewport" } }}
              >
                <div onMouseEnter={clearCloseTimer} onMouseLeave={scheduleClose} onKeyDown={handleEscape}>
                    <Paper id={`navigation-panel-${category.id}`} data-testid={`navigation-flyout-${category.id}`} className={classes.flyout} role="menu" elevation={8}>
                      <Typography className={classes.flyoutTitle}>{category.label}</Typography>
                      {renderEntries(category.children, category.id)}
                    </Paper>
                </div>
              </Popper>
            )}
          </div>
        );
      })}
    </div>
  );

  return <ClickAwayListener onClickAway={() => { if (!isMobile) closeFlyout(); }}>{navigation}</ClickAwayListener>;
};

export default CategoryNavigation;
